import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, of, combineLatest } from 'rxjs';
import { map as pipeMap, catchError, switchMap, mapTo, tap } from 'rxjs/operators';
import { get, map, truncate } from 'lodash-es';
import { ProjectResponse, ProjectSearchResponse } from './projects.response';
import { SyncApiService } from 'src/app/shared/services/sync-api.service';
import {
  Collaborator,
  ItemDtos,
  PlaylistItemsParams,
  PlaylistItemsResponse,
  Project,
  ProjectUpdateDTO,
  ProjectPlaylistUpdateDTO,
  SyncTypeEnum,
  IPlaylistSongsPager,
  ProjectStateFlags,
  ProjectDeletePermission,
  IPitchHistory,
} from './projects.model';
import { ContactType } from '../contacts/types';
import { ProjectSort, TypeRequestEnum } from './enum';
import { ProjectSearchParams, UpdateCollaboratorsResponse } from './types';
import { Page, TPagedResponse } from 'src/app/shared/types/page';
import { AvailableScope, DataErrorService } from 'src/app/shared/data-error';
import { Recording } from '../../recordings/state';
import { SONGS_IN_PROJECT_PAGE_SIZE } from './constants';
import { GroupUpdateDto } from 'src/app/contact-management/groups-management/models/group.model';
import { SongStatus } from '../types/songs';
import { Attachment } from '../attachments/models/attachment.model';
import { isNull, isTrue } from 'src/app/search/musimap-filters/musimap-common/musimap-common.utils';
import { EAttachmentsCategory } from '../attachments/constants/attachments-category.enum';
import { IAttachmentDTO, EAttachmentType } from '../attachments/attachments-form';
import { AttachmentsRepository } from '../attachments/state/attachments.repository';
import { TOrder, TSortOption } from '../../playlist/enum';
import { API_COMMON } from '../../abstract/shared/enum';
import { ToastService } from '../toast';

const SHOW_ERROR_DELAY = 8000;

/** Backend message returned when the reorder request no longer matches the server's playlist. */
const REORDER_MISMATCH_MESSAGE = /mismatched project data/i;

export function transformToSong(item: ItemDtos): Recording {
  return {
    ...item.recording,
    note: item.note,
    position: item.position,
    recordingStatus: item.recordingStatus as SongStatus,
    playbackCount: item.playbackCount,
    downloadCount: item.downloadCount,
    requestedTime: item.requestedTime ? item.requestedTime : null,
    hasLyrics: get(item, 'recording.hasLyrics', true),

    addedToPlaylistBy: item.addedToPlaylistBy || null,
    addedToPlaylistAt: item.addedToPlaylistAt || null,
    profitabilityScore: item.profitabilityScore || null,
  };
}

export function updateSongsPager(
  songsPager: IPlaylistSongsPager,
  playlistItems: PlaylistItemsResponse,
  isSorting: boolean = false
): IPlaylistSongsPager {
  const songs: Recording[] = playlistItems.content.map(transformToSong);

  songsPager.songs = isSorting ? songs : (songsPager.songs || []).concat(songs);
  songsPager.limit = playlistItems.size;
  songsPager.offset = playlistItems.pageable.offset;
  songsPager.total = playlistItems.totalElements;

  return songsPager;
}

export function transformToSongs(
  projects: Project[],
  playlistItems?: PlaylistItemsResponse,
  isSorting: boolean = false
): Project[] | undefined {
  if (playlistItems) {
    return projects.map((project: Project) => {
      let songs: Recording[];
      if (!project.songs || isSorting) {
        songs = playlistItems.content.map(transformToSong);
      } else {
        const existingSongs: Set<number> = new Set<number>((project?.songs || []).map(d => d.id));
        songs = project.songs.concat(
          playlistItems.content.filter(d => !existingSongs.has(d.recording.id)).map(transformToSong)
        );
      }

      return {
        ...project,
        songs,
        songCount: playlistItems.totalElements,
        songsPager: {
          limit: playlistItems.size,
          offset: playlistItems.pageable.offset,
          total: playlistItems.totalElements,
        },
      };
    });
  }

  return map(projects, (project: Project) => {
    const songs = get(project, 'playlist.items', []).map(transformToSong);

    return { ...project, songs, songCount: songs.length };
  });
}

const transformForProjects = (response: TPagedResponse<Project>): TPagedResponse<Project> => {
  const projects = response.content;

  if (!projects) {
    return response;
  }

  return {
    ...response,
    content: transformToSongs(projects),
  };
};

const transformForProject = (response: ProjectResponse, playlistItems?: PlaylistItemsResponse) => {
  const project = response as Project;

  if (!project) {
    return project;
  }
  project.groups = (response?.groups || []).filter(isTrue);

  return transformToSongs([project], playlistItems).pop();
};

export const composeProjectName = (artistName: string, songTitle: string): string => {
  const TRUNCATION_LIMIT = 110;

  const truncatedName = truncate(songTitle, {
    length: TRUNCATION_LIMIT,
    omission: '',
  });
  const truncatedArtist = truncate(artistName, {
    length: TRUNCATION_LIMIT,
    omission: '',
  });

  return `${truncatedName} by ${truncatedArtist}`;
};

export const normalizeAttachment = (attachment: Attachment): Attachment => {
  const category = attachment.uploadLocation;
  const label = attachment.label;

  attachment.category = Object.values(EAttachmentsCategory).includes(category)
    ? category
    : EAttachmentsCategory.GENERAL;
  attachment.label = Object.values(EAttachmentType).includes(label) ? label : null;
  attachment.title = attachment.title || '';
  attachment.description = attachment.description || '';

  switch (attachment.category) {
    case EAttachmentsCategory.INVOICE:
      attachment.invoiceId = attachment.invoiceId || attachment.invoice?.id || null;
      break;
    case EAttachmentsCategory.PAYMENT:
      attachment.paymentId = attachment.paymentId || attachment.payment?.id || null;
      break;
    case EAttachmentsCategory.REQUEST:
      attachment.request = attachment.request || attachment.requests?.[0] || null;
      attachment.requestId = attachment.requestId || attachment.request?.id || null;
      break;
    case EAttachmentsCategory.LICENSING:
    case EAttachmentsCategory.PARTIALLY_EXECUTED:
    case EAttachmentsCategory.FULLY_EXECUTED:
    case EAttachmentsCategory.CUE_SHEET:
      attachment.licenseId = attachment.licenseId || attachment.license?.id || null;
      break;
    default:
      break;
  }

  return attachment;
};

@Injectable({ providedIn: 'root' })
export class ProjectsApiService {
  constructor(
    private api: SyncApiService,
    private attachmentsRepository: AttachmentsRepository,
    private dataErrorService: DataErrorService,
    private toast: ToastService
  ) {}

  fetch(params: { state?: string; syncType?: TypeRequestEnum }, pager?: Page): Observable<TPagedResponse<Project>> {
    const { syncType = null } = params;
    const state = params.state && params.state !== 'all' ? params.state : false;
    const data = state ? { state } : {};
    const syncData = syncType ? { sync_type: syncType } : {};
    const p = pager
      ? {
          page: pager.offset === 0 ? 0 : pager.offset / pager.pageSize,
          size: pager.pageSize,
        }
      : {};

    const opts = {
      ...data,
      ...syncData,
      ...p,
    };
    return this.api
      .get('projects', opts)
      .pipe(pipeMap((response: TPagedResponse<Project>) => transformForProjects(response)));
  }

  fetchById(id: number, songsToFetch: number = 0, includeDeleted: boolean = false, params: any = {}): Observable<any> {
    const fetchAttachments = (projectResponse: Project): Observable<Attachment[]> => {
      return of(projectResponse.attachments || []).pipe(pipeMap(d => d.map(normalizeAttachment)));
    };

    return this.api
      .get<ProjectResponse>(`projects/${id}`, {
        include_playlist_items: false,
        includeDeleted,
      })
      .pipe(
        catchError(() => of(null)),
        switchMap((projectResponse: Project) => {
          if (projectResponse && !projectResponse.deleted) {
            /** GLSS-8042 */
            Object.assign(projectResponse, { readonly: projectResponse.canUndoMerge });

            return fetchAttachments(projectResponse).pipe(
              tap((attachments: Attachment[]) => {
                Object.assign(projectResponse, { attachments });
              }),
              tap((attachments: Attachment[]) => {
                this.attachmentsRepository.forceUpdateAttachments(id, attachments || []);
              }),
              switchMap(() => {
                return this.getSongsForProject(id, {
                  offset: 0,
                  limit: songsToFetch || SONGS_IN_PROJECT_PAGE_SIZE,
                  ...params,
                });
              }),
              pipeMap((playlistItems: PlaylistItemsResponse) => {
                return transformForProject(projectResponse, playlistItems);
              })
            );
          } else {
            return of(projectResponse);
          }
        })
      );
  }

  // addAttachments(projectId: number, attachments: Partial<IAttachmentDTO>[]): Observable<any> {
  //   return forkJoin(
  //     asArray<Partial<IAttachmentDTO>>(attachments).map(d => {
  //       const formData = new FormData();
  //
  //       formData.append('uploadLocation', d.category);
  //       formData.append('label', d.label);
  //       formData.append('title', d.title || '');
  //       formData.append('description', d.description || '');
  //       formData.append('name', d.file.name);
  //       formData.append('file', d.file);
  //
  //       return this.api.postFormData(`projects/${projectId}/attachments`, formData).pipe(pipeMap(normalizeAttachment));
  //     })
  //   ).pipe(tap(f => console.log(f)));
  // }

  getAttachments(projectId: number): Observable<IAttachmentDTO[]> {
    return this.api.get(`projects/${projectId}/attachments`).pipe(
      catchError(err => {
        console.log(err);
        return of(null);
      }),
      switchMap(data => (isNull(data) ? this.api.get(`projects/${projectId}`) : of({ attachments: data }))),
      pipeMap((data: Project) => data.attachments.map(normalizeAttachment))
    );
  }

  // editAttachments(projectId: number, attachments: IAttachmentUpdateDTO[]): Observable<IAttachmentDTO[]> {
  //   return this.api
  //     .patch(`projects/${projectId}/attachments`, { attachments })
  //     .pipe(pipeMap((editedAttachments: Attachment[]) => editedAttachments.map(normalizeAttachment)));
  // }
  //
  // deleteAttachments(projectId: number, ids: string): Observable<any> {
  //   return this.api.delete(`projects/${projectId}/attachments?ids=${ids}`);
  // }

  getProjectStateFlags(id: number): Observable<ProjectStateFlags> {
    return combineLatest([
      this.api
        .get<ProjectResponse>(`projects/${id}`, {
          include_playlist_items: false,
        })
        .pipe(),
      this.api.get<ProjectDeletePermission>(`projects/${id}/checkDelete`).pipe(
        catchError(err =>
          of({
            canBeDeleted: false,
            message: err.message || 'Uncaught Error',
          })
        )
      ),
    ]).pipe(
      pipeMap(([{ projectStateFlags }, { canBeDeleted, message }]) => {
        return {
          ...projectStateFlags,
          message,
          lockedFlag: !canBeDeleted,
        };
      })
    );
  }

  search(params: ProjectSearchParams, pager: Page): Observable<ProjectSearchResponse> {
    const {
      state,
      sync_type,
      query,
      group_name,
      has_recording_id,
      sort_type = ProjectSort.DATE,
      is_mine = true,
      is_collaborator = false,
      is_all_else = false,
      is_group = false,
      territory_id,
      recording_title,
      recording_artist,
      recording_writer,
    } = params;
    const { offset = 0, pageSize = 8 } = pager;

    const requestParams = {
      ...(has_recording_id && { has_recording_id }),
      ...(query && { query }),
      ...(group_name && { group_name }),
      ...(sync_type && { sync_type }),
      ...(state && { state }),
      ...(recording_title && { recording_title }),
      ...(recording_artist && { recording_artist }),
      ...(recording_writer && { recording_writer }),
      ...(territory_id && { territory_id }),
      offset,
      limit: pageSize,
      sort_type,
      is_mine,
      is_collaborator,
      is_all_else,
      is_group,
    };
    const searchParams = requestParams.territory_id
      ? { ...requestParams, territory_id: requestParams.territory_id.id }
      : requestParams;

    const url = 'projects/search';
    return this.api.get(url, searchParams);
  }

  createProject(name: string, syncType?: SyncTypeEnum): Observable<ProjectResponse> {
    return this.api.post('projects', {
      name,
      syncType,
    });
  }

  attachSong(projectId: number, songIds: { songId: number }[]): Observable<any> {
    return this.api.post(`projects/${projectId}/playlist/items`, songIds);
  }

  detachSong(projectId: number, songId: number): Observable<any> {
    return this.api.delete(`projects/${projectId}/playlist/songs/${songId}`);
  }

  detachSongBulk(projectId: number, songIds: number[]): Observable<any> {
    const songIdsParam = songIds.join(',');
    return this.api.delete(`projects/${projectId}/playlist/items/?songIds=${songIdsParam}`);
  }

  attachContact(projectId: number, contactId: number, contactType: ContactType): Observable<any> {
    return this.api.put(`projects/${projectId}/contacts`, { contactId, contactType });
  }

  detachContact(projectId: number, contactId: number): Observable<any> {
    return this.api.delete(`projects/${projectId}/contacts/${contactId}`);
  }

  validateName(name: string, andUser: boolean = true, syncType: SyncTypeEnum = null): Observable<any> {
    let params: any = { andUser };
    syncType && (params = { ...params, syncType });
    return this.api.get(`projects/names/${name}`, params);
  }

  update(params: Partial<ProjectUpdateDTO>): Observable<ProjectResponse> {
    return this.api.put('projects', params).pipe(pipeMap((response: ProjectResponse) => transformForProject(response)));
  }

  updatePlaylist(projectId: number, params: Partial<ProjectPlaylistUpdateDTO>): Observable<ProjectResponse> {
    return this.api
      .put(`projects/${projectId}/playlist/`, params)
      .pipe(pipeMap((response: ProjectResponse) => transformForProject(response)));
  }

  reorderSongs(projectId: number, order: number[], originOrder: number[]): Observable<ProjectResponse> {
    return this.api
      .put(`projects/${projectId}/playlist/reorder`, {
        order,
        originOrder,
      })
      .pipe(
        pipeMap((response: ProjectResponse) => transformForProject(response)),
        pipeMap((project: Project) => project as ProjectResponse)
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 409) {
            this.dataErrorService.updateByScopeCustomConflict(AvailableScope.main, {
              state: true,
              title: 'Data Conflict',
              content:
                'Someone else has edited the playlist at the same time as you did. ' +
                'We updated the playlist for you. You can now retry your last action.',
              linkTitle: '',
            });

            setTimeout(
              () => this.dataErrorService.updateByScope(AvailableScope.main, { state: false }),
              SHOW_ERROR_DELAY
            );
          } else if (REORDER_MISMATCH_MESSAGE.test(error?.error?.message || error?.message || '')) {
            this.toast.error(
              'This playlist was changed or something went wrong. Please refresh the page and try again.'
            );
          }

          return throwError(error);
        })
      );
  }

  updateCollaborators(projectId: number, collaborators: Collaborator[]): Observable<Collaborator[]> {
    return this.api
      .put<UpdateCollaboratorsResponse>(`projects/${projectId}/collaborators`, {
        collaborators,
      })
      .pipe(pipeMap(data => data.collaborators));
  }

  createProjectForSongPitch(song: Recording): Observable<Project> {
    const projectName = composeProjectName(song.artists.join(', '), song.title);

    return this.createProject(projectName).pipe(
      switchMap(res => this.attachSong(res.id, [{ songId: song.id }]).pipe(mapTo(res)))
    );
  }

  getSongsForProject(projectId: number, params?: PlaylistItemsParams): Observable<PlaylistItemsResponse> {
    const { offset = 0, limit = SONGS_IN_PROJECT_PAGE_SIZE } = params;
    return this.api.get<PlaylistItemsResponse>(`projects/${projectId}/playlist/items`, {
      ...params,
      offset,
      limit,
    });
  }

  updateAlbumArt(projectId: number, params: Partial<Project>): Observable<any> {
    return this.api.put(`projects/${projectId}`, params);
  }

  generateExternalPitchURL(projectId: number): Observable<any> {
    return this.api.post(`projects/${projectId}/generate_pitch_link`);
  }

  updateGroups(projectId: number, groupsUpdateDto: GroupUpdateDto[]): Observable<string> {
    return this.api.put(`projects/${projectId}/groups`, { groupsId: groupsUpdateDto });
  }

  updateLockStatus(projectId: number, params: Partial<Project>): Observable<any> {
    return this.api.put(`projects/${projectId}/locked`, params);
  }

  getSongsToFetchCount(songsLoaded: number): number {
    const mod = songsLoaded % SONGS_IN_PROJECT_PAGE_SIZE;
    return mod !== 0 ? songsLoaded + SONGS_IN_PROJECT_PAGE_SIZE - mod : songsLoaded;
  }

  getSapTerritories(): any {
    return this.api.get('sap-territories');
  }

  /**
   * Delete Project or Playlist
   * @param projectId deletable project id
   */
  delete(projectId: number): Observable<any> {
    return this.api.delete(`projects/${projectId}`);
  }

  // /**
  //  * Duplicate Project
  //  * @param params duplication options
  //  */
  // duplicate(params: IProjectDuplicateDTO): Observable<any> {
  //   return this.api.post(`projects/duplicate`, params);
  // }

  /**
   * Get playlist origin order after sorting
   * @param projectId project id
   * @param sortField PLAYLIST_ORDER | SONG_TITLE ...etc
   * @param sortOrder asc | desc
   */
  getPlaylistOriginOrder(projectId: number, sortField: TSortOption, sortOrder: TOrder): Observable<number[]> {
    sortField = encodeURI(sortField) as TSortOption;
    return this.api.get(`projects/${projectId}/playlist/item-recording-ids`, {
      sortField,
      sortOrder,
    });
  }

  getPitchHistory(projectId: number): Observable<IPitchHistory[]> {
    return this.api.get(`projects/${projectId}/pitch_history`);
  }

  updatePitchHistory(projectId: number, historyParams: IPitchHistory): Observable<IPitchHistory[]> {
    return this.api.post(`projects/${projectId}/pitch_history`, historyParams);
  }

  /** GLSS-4149 */
  public prepareMerge = (name: string, projectIds: number[]): Observable<{ projectId: any; error?: any }> => {
    const { api } = this;
    return api.post([API_COMMON.PROJECTS, 'merge'].join('/'), { name, projectIds }).pipe(
      catchError(error => {
        console.log(error);
        return of(error);
      })
    );
  };

  public undoMerge = (projectId: any): Observable<any> => {
    const { api } = this;
    const url = [API_COMMON.PROJECTS, projectId, 'merge', 'undo'].join('/');

    return api.post(url, {}).pipe(
      catchError(error => {
        console.log(error);
        return of(error);
      })
    );
  };

  public confirmMerge = (projectId: any): Observable<any> => {
    const { api } = this;
    const url = [API_COMMON.PROJECTS, projectId, 'merge', 'confirm'].join('/');

    return api.patch(url, {}).pipe(
      catchError(error => {
        console.log(error);
        return of(error);
      })
    );
  };
}
